/**
 * Formatters centralizados para toda a aplicação
 * 
 * REGRAS:
 * - Todas as funções devem lidar graciosamente com valores nulos/undefined
 * - Locale padrão: pt-BR
 * - Moeda padrão: BRL (R$)
 */

/**
 * Formata data para exibição no formato brasileiro
 * 
 * @param {string|Date} dateString - Data em ISO string ou Date object
 * @returns {string} Data formatada (dd/mm/yyyy) ou string vazia se inválida
 * 
 * @example
 * formatDate("2025-01-15") // "15/01/2025"
 * formatDate(new Date()) // "15/01/2025"
 */
export function formatDate(dateString) {
  if (!dateString) return '';
  // Parse date-only strings (YYYY-MM-DD) directly to avoid UTC→local timezone shift
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const [year, month, day] = dateString.substring(0, 10).split('-').map(Number);
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  }
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formata data e hora para exibição no formato brasileiro
 * 
 * @param {string|Date} dateString - Data em ISO string ou Date object
 * @param {boolean} useTimezone - Se deve usar timezone America/Sao_Paulo (default: true)
 * @returns {string} Data e hora formatadas (dd/mm/yyyy HH:mm) ou string vazia se inválida
 * 
 * @example
 * formatDateTime("2025-01-15T14:30:00Z") // "15/01/2025 11:30" (BRT)
 */
export function formatDateTime(dateString, useTimezone = true) {
  if (!dateString) return '';
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '';
  
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  
  if (useTimezone) {
    options.timeZone = 'America/Sao_Paulo';
  }
  
  return date.toLocaleString('pt-BR', options);
}

/**
 * Formata horário de uma data ISO para exibição
 * 
 * @param {string|Date} dateString - Data em ISO string ou Date object
 * @param {boolean} useTimezone - Se deve usar timezone America/Sao_Paulo (default: true)
 * @returns {string} Horário formatado (HH:mm) ou '--:--' se inválido
 * 
 * @example
 * formatTime("2025-01-15T14:30:00Z") // "11:30" (BRT)
 */
export function formatTime(dateString, useTimezone = true) {
  if (!dateString) return '--:--';
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  if (isNaN(date.getTime())) return '--:--';
  
  const options = {
    hour: '2-digit',
    minute: '2-digit',
  };
  
  if (useTimezone) {
    options.timeZone = 'America/Sao_Paulo';
  }
  
  return date.toLocaleTimeString('pt-BR', options);
}

/**
 * Formata TimeSpan do .NET (string HH:mm:ss) para exibição
 * 
 * @param {string} timeSpan - TimeSpan no formato "HH:mm:ss" ou "HH:mm"
 * @returns {string} Horário formatado (HH:mm) ou string vazia se inválido
 * 
 * @example
 * formatTimeSpan("08:30:00") // "08:30"
 * formatTimeSpan("14:00") // "14:00"
 */
export function formatTimeSpan(timeSpan) {
  if (!timeSpan) return '';
  if (typeof timeSpan === 'string') {
    return timeSpan.substring(0, 5);
  }
  return timeSpan;
}

/**
 * Formata valor monetário para Real Brasileiro
 * 
 * @param {number|string} value - Valor numérico ou string numérica
 * @returns {string} Valor formatado (R$ X,XX) ou '-' se inválido
 * 
 * @example
 * formatCurrency(150.5) // "R$ 150,50"
 * formatCurrency("99.99") // "R$ 99,99"
 */
export function formatCurrency(value) {
  if (value == null || value === '') return '-';
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '-';
  return `R$ ${numValue.toFixed(2).replace('.', ',')}`;
}

/**
 * Formata duração em minutos para formato legível
 * 
 * @param {number} minutes - Duração em minutos
 * @returns {string} Duração formatada (Xh Ymin) ou '-' se inválido
 * 
 * @example
 * formatDuration(90) // "1h 30min"
 * formatDuration(45) // "45min"
 * formatDuration(120) // "2h"
 */
export function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '-';
  const mins = parseInt(minutes, 10);
  if (isNaN(mins) || mins < 0) return '-';
  
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  
  if (hours > 0 && remainingMins > 0) return `${hours}h ${remainingMins}min`;
  if (hours > 0) return `${hours}h`;
  return `${remainingMins}min`;
}

/**
 * Calcula a diferença em dias entre duas datas
 * 
 * @param {string|Date} startDate - Data inicial
 * @param {string|Date} endDate - Data final
 * @returns {string} Diferença formatada (X dia/dias) ou string vazia se inválido
 * 
 * @example
 * getDurationInDays("2025-01-01", "2025-01-05") // "4 dias"
 * getDurationInDays("2025-01-01", "2025-01-02") // "1 dia"
 */
export function getDurationInDays(startDate, endDate) {
  if (!startDate || !endDate) return '';
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
  
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return `${diffDays} dia${diffDays !== 1 ? 's' : ''}`;
}

/**
 * Formata header de dia para calendários
 * 
 * @param {Date} date - Date object
 * @returns {{weekday: string, dayNum: number}} Objeto com dia da semana e número do dia
 * 
 * @example
 * formatDayHeader(new Date('2025-01-15')) // { weekday: "qua", dayNum: 15 }
 */
export function formatDayHeader(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return { weekday: '', dayNum: 0 };
  }
  const weekday = date.toLocaleDateString('pt-BR', { weekday: 'short' });
  const dayNum = date.getDate();
  return { weekday, dayNum };
}

/**
 * Formata mês e ano para exibição
 * 
 * @param {Date} date - Date object
 * @returns {string} Mês e ano formatados (ex: "janeiro de 2025")
 * 
 * @example
 * formatMonthYear(new Date('2025-01-15')) // "janeiro de 2025"
 */
export function formatMonthYear(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

/**
 * Calcula idade a partir da data de nascimento
 * 
 * @param {string|Date} birthDate - Data de nascimento
 * @returns {number|null} Idade em anos ou null se inválido
 * 
 * @example
 * getAge("1990-05-15") // 34 (dependendo da data atual)
 */
export function getAge(birthDate) {
  if (!birthDate) return null;
  const today = new Date();
  // Parse date-only strings directly to avoid timezone shift
  let birthYear, birthMonth, birthDay;
  if (typeof birthDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(birthDate)) {
    [birthYear, birthMonth, birthDay] = birthDate.substring(0, 10).split('-').map(Number);
    birthMonth -= 1; // JS months are 0-indexed
  } else {
    const birth = new Date(birthDate);
    if (isNaN(birth.getTime())) return null;
    birthYear = birth.getFullYear();
    birthMonth = birth.getMonth();
    birthDay = birth.getDate();
  }

  let age = today.getFullYear() - birthYear;
  const monthDiff = today.getMonth() - birthMonth;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDay)) {
    age--;
  }
  return age;
}

/**
 * Formata data/hora para formato relativo (tempo atrás)
 * 
 * @param {string|Date} dateString - Data a formatar
 * @returns {string} Tempo relativo (ex: "5min atrás", "2h atrás", "3d atrás")
 * 
 * @example
 * formatRelativeTime(new Date(Date.now() - 5 * 60000)) // "5min atrás"
 * formatRelativeTime(new Date(Date.now() - 2 * 3600000)) // "2h atrás"
 */
export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `${diffMins}min atrás`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h atrás`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d atrás`;
    
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
}
